/** Work order tabs added in Phase 2: Notes (with Additional Issues), Time (Manager edits), Photos. */
import { useState } from 'react';
import type { Me } from '@wo/shared';
import { api, errorText } from '../lib/api';
import { dateTime } from '../lib/format';
import { useMechanics } from '../lib/hooks';
import { Button, Card, ErrorText, Field, Input, Modal, Select, Textarea } from '../components/ui';
import { can } from '../lib/perm';

export interface WoNote {
  id: number;
  kind: string;
  bodyClean: string;
  bodyRawTranscript: string | null;
  submittedAt: string;
  offline: boolean;
  correctsNoteId: number | null;
  user: { id: number; name: string };
  resolution: { resolution: string; comment: string | null; linkedWorkOrderId: number | null } | null;
}
export interface WoPunch {
  id: number;
  source: 'CLOCK' | 'MANUAL';
  startAt: string;
  endAt: string | null;
  offlineStart: boolean;
  offlineEnd: boolean;
  clockSkewMs: number | null;
  edited: boolean;
  user: { id: number; name: string };
  edits: { id: number; oldStart: string | null; oldEnd: string | null; newStart: string; newEnd: string | null; reason: string; at: string }[];
}
export interface WoFile {
  id: number;
  kind: string;
  purpose: string;
  mime: string;
  capturedAt: string | null;
  exifTime: string | null;
  offline: boolean;
}

const KIND_LABEL: Record<string, string> = {
  WORK_PERFORMED: 'Work performed',
  ADDITIONAL_ISSUE: 'Additional issue',
  INTERNAL: 'Shop note',
  OFFICE_ONLY: 'Internal (office only)',
  CORRECTION: 'Correction',
  PROBLEM: 'Problem',
};
const hours = (p: { startAt: string; endAt: string | null }) => ((p.endAt ? new Date(p.endAt).getTime() : Date.now()) - new Date(p.startAt).getTime()) / 3_600_000;
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

export function NotesTab({ woId, notes, me, readOnly, onChange }: { woId: number; notes: WoNote[]; me: Me; readOnly: boolean; onChange: () => void }) {
  const [kind, setKind] = useState('OFFICE_ONLY');
  const [body, setBody] = useState('');
  const [corrects, setCorrects] = useState<WoNote | null>(null);
  const [resolving, setResolving] = useState<WoNote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isManager = can(me, 'jobs.review');

  const add = async () => {
    setError(null);
    try {
      await api.post(`/api/work-orders/${woId}/notes`, corrects ? { kind: 'CORRECTION', body, correctsNoteId: corrects.id } : { kind, body });
      setBody('');
      setCorrects(null);
      onChange();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <div className="space-y-4">
      <Card title="Notes">
        {notes.length === 0 && <p className="text-sm text-slate-500">No notes yet.</p>}
        <ul className="space-y-3">
          {notes.map((n) => (
            <li key={n.id} className={`rounded-md border p-3 ${n.kind === 'ADDITIONAL_ISSUE' && !n.resolution ? 'border-amber-400 bg-amber-50' : n.kind === 'OFFICE_ONLY' ? 'border-violet-300 bg-violet-50' : 'border-slate-200'}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span className="font-semibold uppercase">{KIND_LABEL[n.kind] ?? n.kind}</span>
                <span>{n.user.name}</span>
                <span>{dateTime(n.submittedAt)}</span>
                {n.offline && <span className="rounded bg-slate-200 px-1">offline</span>}
                {n.correctsNoteId && <span>corrects note #{n.correctsNoteId}</span>}
                <span className="ml-auto">#{n.id}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm">{n.bodyClean}</p>
              {n.bodyRawTranscript && <details className="mt-1 text-xs text-slate-500"><summary>Word-for-word transcript</summary>{n.bodyRawTranscript}</details>}
              {n.kind === 'ADDITIONAL_ISSUE' && (
                n.resolution ? (
                  <p className="mt-1 text-xs text-green-800">Handled: {n.resolution.resolution.replace(/_/g, ' ').toLowerCase()}{n.resolution.comment && ` — ${n.resolution.comment}`}</p>
                ) : (
                  isManager && <Button variant="secondary" className="mt-2" onClick={() => setResolving(n)}>Decide what to do</Button>
                )
              )}
              {!readOnly && <button className="mt-1 text-xs text-blue-700 hover:underline" onClick={() => setCorrects(n)}>Add correction</button>}
            </li>
          ))}
        </ul>
      </Card>
      {!readOnly && (
        <Card title={corrects ? `Correction to note #${corrects.id}` : 'Add a note'}>
          <ErrorText error={error} />
          {!corrects && (
            <Field label="Type">
              <Select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="OFFICE_ONLY">Internal (office only) — mechanics never see it; goes to billing</option>
                <option value="INTERNAL">Shop note — mechanics see it</option>
                <option value="WORK_PERFORMED">Work performed</option>
              </Select>
            </Field>
          )}
          <Field label="Note"><Textarea value={body} onChange={(e) => setBody(e.target.value)} /></Field>
          <div className="mt-2 flex justify-end gap-2">
            {corrects && <Button variant="secondary" onClick={() => setCorrects(null)}>Cancel</Button>}
            <Button onClick={add} disabled={!body.trim()}>Save note</Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">Notes can't be edited once saved. Mistakes get a correction note; both stay on the record.</p>
        </Card>
      )}
      {resolving && <ResolveDialog note={resolving} onClose={() => setResolving(null)} onDone={() => { setResolving(null); onChange(); }} />}
    </div>
  );
}

function ResolveDialog({ note, onClose, onDone }: { note: WoNote; onClose: () => void; onDone: () => void }) {
  const [resolution, setResolution] = useState('ADDED_TO_THIS_JOB');
  const [linked, setLinked] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    try {
      let linkedWorkOrderId: number | undefined;
      if (resolution === 'NEW_WORK_ORDER') {
        const found = await api.get<{ id: number }[]>(`/api/work-orders?q=${encodeURIComponent(linked)}&limit=1`);
        if (!found[0]) throw new Error(`No work order #${linked}`);
        linkedWorkOrderId = found[0].id;
      }
      await api.post(`/api/notes/${note.id}/resolve`, { resolution, linkedWorkOrderId, comment: comment || undefined });
      onDone();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal title="Additional issue" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save}>Save</Button></>}>
      <p className="rounded bg-amber-50 p-2 text-sm">“{note.bodyClean}”</p>
      <ErrorText error={error} />
      <Field label="Decision">
        <Select value={resolution} onChange={(e) => setResolution(e.target.value)}>
          <option value="ADDED_TO_THIS_JOB">Do it on this work order</option>
          <option value="NEW_WORK_ORDER">Opened a separate work order</option>
          <option value="NO_ACTION">No action needed</option>
        </Select>
      </Field>
      {resolution === 'NEW_WORK_ORDER' && <Field label="New work order number" hint="Create it first under New Work Order."><Input value={linked} onChange={(e) => setLinked(e.target.value.replace(/\D/g, ''))} /></Field>}
      <Field label="Comment" required={resolution === 'NO_ACTION'}><Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
    </Modal>
  );
}

export function TimeTab({ woId, punches, me, readOnly, onChange }: { woId: number; punches: WoPunch[]; me: Me; readOnly: boolean; onChange: () => void }) {
  const canEdit = can(me, 'time.edit') && !readOnly;
  const [editing, setEditing] = useState<WoPunch | 'new' | null>(null);
  const total = punches.filter((p) => p.endAt).reduce((a, p) => a + hours(p), 0);
  return (
    <Card title={`Time — ${total.toFixed(2)} h`} actions={canEdit && <Button variant="secondary" onClick={() => setEditing('new')}>Add time</Button>}>
      {punches.length === 0 && <p className="text-sm text-slate-500">No time recorded.</p>}
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-slate-500">
          <tr><th className="py-1">Mechanic</th><th>Start</th><th>Finish</th><th>Hours</th><th>Flags</th><th /></tr>
        </thead>
        <tbody className="divide-y">
          {punches.map((p) => (
            <tr key={p.id} className="align-top">
              <td className="py-2">{p.user.name}</td>
              <td>{dateTime(p.startAt)}</td>
              <td>{p.endAt ? dateTime(p.endAt) : <span className="font-semibold text-green-700">clocked in</span>}</td>
              <td>{hours(p).toFixed(2)}</td>
              <td className="space-x-1 text-xs">
                {p.source === 'MANUAL' && <span className="rounded bg-purple-100 px-1">manual</span>}
                {p.edited && <span className="rounded bg-amber-100 px-1" title={p.edits.map((e) => `${dateTime(e.at)}: ${e.reason}`).join('\n')}>edited</span>}
                {(p.offlineStart || p.offlineEnd) && <span className="rounded bg-slate-200 px-1">offline punch</span>}
                {p.clockSkewMs !== null && Math.abs(p.clockSkewMs) > 5 * 60_000 && <span className="rounded bg-red-100 px-1">iPad clock off {Math.round(p.clockSkewMs / 60_000)} min</span>}
              </td>
              <td className="text-right">{canEdit && <Button variant="ghost" onClick={() => setEditing(p)}>Edit</Button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {punches.some((p) => p.edits.length) && (
        <details className="mt-3 text-xs text-slate-600">
          <summary>Edit history (originals kept)</summary>
          <ul className="mt-1 space-y-1">
            {punches.flatMap((p) => p.edits.map((e) => (
              <li key={e.id}>
                {p.user.name}: {e.oldStart ? `${dateTime(e.oldStart)}–${e.oldEnd ? dateTime(e.oldEnd) : 'open'}` : '(new)'} → {dateTime(e.newStart)}–{e.newEnd ? dateTime(e.newEnd) : 'open'} · “{e.reason}”
              </li>
            )))}
          </ul>
        </details>
      )}
      {editing && <PunchDialog woId={woId} punch={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); onChange(); }} />}
    </Card>
  );
}

function PunchDialog({ woId, punch, onClose, onDone }: { woId: number; punch: WoPunch | null; onClose: () => void; onDone: () => void }) {
  const mechanics = useMechanics(!punch);
  const [userId, setUserId] = useState('');
  const [start, setStart] = useState(punch ? toLocalInput(punch.startAt) : '');
  const [end, setEnd] = useState(punch?.endAt ? toLocalInput(punch.endAt) : '');
  const [moveTo, setMoveTo] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const iso = (v: string) => new Date(v).toISOString();
  const save = async () => {
    try {
      if (punch) {
        let workOrderId: number | undefined;
        if (moveTo) {
          const found = await api.get<{ id: number; number: number }[]>(`/api/work-orders?q=${moveTo}&limit=1`);
          if (!found[0] || String(found[0].number) !== moveTo) throw new Error(`No work order #${moveTo}`);
          workOrderId = found[0].id;
        }
        await api.patch(`/api/punches/${punch.id}`, { startAt: iso(start), endAt: end ? iso(end) : null, workOrderId, reason });
      } else {
        await api.post(`/api/work-orders/${woId}/punches`, { userId: Number(userId), startAt: iso(start), endAt: iso(end), reason });
      }
      onDone();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal title={punch ? `Edit time — ${punch.user.name}` : 'Add time'} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={reason.trim().length < 3 || !start || (!punch && (!end || !userId))}>Save</Button></>}>
      <ErrorText error={error} />
      {!punch && (
        <Field label="Mechanic" required>
          <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Choose…</option>
            {mechanics.data?.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </Select>
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Start" required><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
        <Field label="Finish" required={!punch}><Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
      </div>
      {punch && <Field label="Move to work order # (optional)" hint="Use this before voiding a job that has time on it."><Input value={moveTo} onChange={(e) => setMoveTo(e.target.value.replace(/\D/g, ''))} /></Field>}
      <Field label="Reason" required><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      <p className="text-xs text-slate-500">The original times are kept and shown in the edit history.</p>
    </Modal>
  );
}

export function PhotosTab({ files }: { files: WoFile[] }) {
  const photos = files.filter((f) => f.kind === 'PHOTO');
  const [big, setBig] = useState<WoFile | null>(null);
  return (
    <Card title={`Photos (${photos.length})`}>
      {photos.length === 0 && <p className="text-sm text-slate-500">No photos yet.</p>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {photos.map((f) => (
          <button key={f.id} onClick={() => setBig(f)} className="overflow-hidden rounded-md border text-left">
            <img src={`/api/files/${f.id}`} alt={f.purpose} loading="lazy" className="aspect-[4/3] w-full object-cover" />
            <div className="p-1 text-xs text-slate-600">
              {f.purpose.replace('_', ' ')} · {dateTime(f.capturedAt)}
              {!f.exifTime && <span className="ml-1 text-amber-700">no camera time</span>}
            </div>
          </button>
        ))}
      </div>
      {big && (
        <Modal title={`${big.purpose.replace('_', ' ')} — ${dateTime(big.capturedAt)}`} onClose={() => setBig(null)}>
          <img src={`/api/files/${big.id}`} alt="" className="w-full" />
          <a className="text-sm text-blue-700 underline" href={`/api/files/${big.id}?original=1`} target="_blank" rel="noreferrer">Open original</a>
        </Modal>
      )}
    </Card>
  );
}
